import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { useAuth } from './AuthContext';
import {
  Partner,
  PartnerSettings,
  PartnerIntegrations,
  IntegrationStatus
} from '../types/partner.types';
import {
  getPartner,
  getIntegrationStatus,
  hasRequiredIntegrations
} from '../services/partner/partnerService';
import { UserRole } from '../types/user.types';

interface PartnerContextType {
  partner: Partner | null;
  loading: boolean;
  error: string | null;
  integrations: {
    sendgrid: IntegrationStatus;
    meta: IntegrationStatus;
    smsPortal: IntegrationStatus;
    whatsapp: IntegrationStatus;
  } | null;
  settings: PartnerSettings | null;
  // Integration check helpers
  hasEmailIntegration: boolean;
  hasSMSIntegration: boolean;
  hasWhatsAppIntegration: boolean;
  hasFacebookIntegration: boolean;
  // Refresh functions
  refreshPartner: () => Promise<void>;
  refreshIntegrations: () => Promise<void>;
  // Admin: Switch to different partner context (for super users)
  switchPartner: (partnerId: string) => Promise<void>;
  currentPartnerId: string | null;
}

const PartnerContext = createContext<PartnerContextType>({
  partner: null,
  loading: true,
  error: null,
  integrations: null,
  settings: null,
  hasEmailIntegration: false,
  hasSMSIntegration: false,
  hasWhatsAppIntegration: false,
  hasFacebookIntegration: false,
  refreshPartner: async () => {},
  refreshIntegrations: async () => {},
  switchPartner: async () => {},
  currentPartnerId: null
});

export const usePartner = () => {
  const context = useContext(PartnerContext);
  if (!context) {
    throw new Error('usePartner must be used within a PartnerProvider');
  }
  return context;
};

// Convenience hooks for specific parts of partner data
export const usePartnerIntegrations = () => {
  const { integrations, hasEmailIntegration, hasSMSIntegration, hasWhatsAppIntegration, hasFacebookIntegration } = usePartner();
  return { integrations, hasEmailIntegration, hasSMSIntegration, hasWhatsAppIntegration, hasFacebookIntegration };
};

export const usePartnerSettings = () => {
  const { settings } = usePartner();
  return settings;
};

export const PartnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userProfile, loading: authLoading } = useAuth();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<{
    sendgrid: IntegrationStatus;
    meta: IntegrationStatus;
    smsPortal: IntegrationStatus;
    whatsapp: IntegrationStatus;
  } | null>(null);

  // For admin users who can switch between partners
  const [currentPartnerId, setCurrentPartnerId] = useState<string | null>(null);

  // Integration availability flags
  const [hasEmailIntegration, setHasEmailIntegration] = useState(false);
  const [hasSMSIntegration, setHasSMSIntegration] = useState(false);
  const [hasWhatsAppIntegration, setHasWhatsAppIntegration] = useState(false);
  const [hasFacebookIntegration, setHasFacebookIntegration] = useState(false);

  // Determine which partnerId to use
  // HQ users (super users, managers, admins, etc.) should default to 'propinvest_hq' if no partnerId is set
  const effectivePartnerId = currentPartnerId || userProfile?.partnerId ||
    (userProfile?.role && [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_USER_MANAGEMENT,
      UserRole.PROPCLOUDAI_CRM,
      UserRole.PROPCLOUDAI_ADVERTISING,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_REFERRAL_MANDATE,
      UserRole.HQ_VIEWING_COORDINATOR
    ].includes(userProfile.role) ? 'propinvest_hq' : null);

  // Check if user can switch partners (super users only)
  const canSwitchPartners = userProfile?.role === UserRole.PROPCLOUDAI_SUPER_USER;

  // Load integration status
  const loadIntegrationStatus = useCallback(async (partnerId: string) => {
    try {
      const status = await getIntegrationStatus(partnerId);
      setIntegrations(status);

      // Check integration capabilities
      const [email, sms, whatsapp, facebook] = await Promise.all([
        hasRequiredIntegrations(partnerId, 'email'),
        hasRequiredIntegrations(partnerId, 'sms'),
        hasRequiredIntegrations(partnerId, 'whatsapp'),
        hasRequiredIntegrations(partnerId, 'facebook')
      ]);

      setHasEmailIntegration(email);
      setHasSMSIntegration(sms);
      setHasWhatsAppIntegration(whatsapp);
      setHasFacebookIntegration(facebook);
    } catch (err) {
      console.error('Failed to load integration status:', err);
    }
  }, []);

  // Manual refresh function for partner data
  const refreshPartner = useCallback(async () => {
    if (!effectivePartnerId) return;

    try {
      const partnerData = await getPartner(effectivePartnerId);

      if (partnerData) {
        setPartner(partnerData);
        setError(null);
        await loadIntegrationStatus(effectivePartnerId);
      } else {
        setPartner(null);
        setError('Partner not found');
      }
    } catch (err: unknown) {
      console.error('Failed to refresh partner:', err);
      setError((err instanceof Error ? err.message : String(err)));
    }
  }, [effectivePartnerId, loadIntegrationStatus]);

  // Manual refresh for integrations only
  const refreshIntegrations = useCallback(async () => {
    if (!effectivePartnerId) return;
    await loadIntegrationStatus(effectivePartnerId);
  }, [effectivePartnerId, loadIntegrationStatus]);

  // Switch partner (admin only)
  const switchPartner = useCallback(async (partnerId: string) => {
    if (!canSwitchPartners) {
      throw new Error('You do not have permission to switch partners');
    }

    try {
      const partnerData = await getPartner(partnerId);
      if (!partnerData) {
        throw new Error('Partner not found');
      }

      setCurrentPartnerId(partnerId);
    } catch (err: unknown) {
      console.error('Failed to switch partner:', err);
      throw err;
    }
  }, [canSwitchPartners]);

  // Load partner data and set up real-time listener
  useEffect(() => {
    if (authLoading || !effectivePartnerId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    let unsubscribe: (() => void) | null = null;

    const loadPartner = async () => {
      try {
        // Initial load
        const partnerData = await getPartner(effectivePartnerId);

        if (partnerData) {
          setPartner(partnerData);
          setError(null);

          // Load integration status
          await loadIntegrationStatus(effectivePartnerId);

          // Set up real-time listener for partner document changes
          unsubscribe = onSnapshot(
            doc(db, 'partners', effectivePartnerId),
            async (snapshot) => {
              if (snapshot.exists()) {
                const updatedPartner = { id: snapshot.id, ...snapshot.data() } as Partner;
                setPartner(updatedPartner);

                // Refresh integration status when partner changes
                await loadIntegrationStatus(effectivePartnerId);
              } else {
                setPartner(null);
                setError('Partner not found');
              }
            },
            (err) => {
              console.error('Error listening to partner changes:', err);
              setError('Failed to sync partner data');
            }
          );
        } else {
          setPartner(null);
          setError('Partner not found');
        }
      } catch (err: unknown) {
        console.error('Failed to load partner:', err);
        setError((err instanceof Error ? err.message : String(err)));
        setPartner(null);
      } finally {
        setLoading(false);
      }
    };

    loadPartner();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [authLoading, effectivePartnerId, loadIntegrationStatus]);

  const value = {
    partner,
    loading,
    error,
    integrations,
    settings: partner?.settings || null,
    hasEmailIntegration,
    hasSMSIntegration,
    hasWhatsAppIntegration,
    hasFacebookIntegration,
    refreshPartner,
    refreshIntegrations,
    switchPartner,
    currentPartnerId: effectivePartnerId
  };

  return (
    <PartnerContext.Provider value={value}>
      {children}
    </PartnerContext.Provider>
  );
};
